/**
 * Focus Trap Hook
 * Traps focus within a modal/dialog for accessibility
 * - Focus first focusable element on mount
 * - Tab cycles through focusable elements
 * - Shift+Tab cycles backwards
 * - Escape closes the modal
 * - Restores focus to previous element on destroy
 */

const FocusTrapHook = {
  mounted() {
    this.previouslyFocused = document.activeElement;
    this.focusableSelector = [
      'a[href]:not([disabled])',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    // Small delay to ensure DOM is ready
    requestAnimationFrame(() => {
      this.trapFocus();
    });

    // Handle keydown for tab trapping and escape
    this.handleKeydown = this.handleKeydown.bind(this);
    this.el.addEventListener('keydown', this.handleKeydown);

    // Prevent focus from escaping
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.el.addEventListener('focusout', this.handleFocusOut);
  },

  destroyed() {
    this.el.removeEventListener('keydown', this.handleKeydown);
    this.el.removeEventListener('focusout', this.handleFocusOut);

    // Restore focus to previous element
    if (this.previouslyFocused && this.previouslyFocused.focus) {
      try {
        this.previouslyFocused.focus();
      } catch (e) {
        // Element may no longer exist
      }
    }
  },

  trapFocus() {
    const focusableElements = this.el.querySelectorAll(this.focusableSelector);

    if (focusableElements.length === 0) return;

    this.firstFocusable = focusableElements[0];
    this.lastFocusable = focusableElements[focusableElements.length - 1];

    // Focus first element (or element with autofocus)
    const autoFocusElement = this.el.querySelector('[autofocus]');
    if (autoFocusElement) {
      autoFocusElement.focus();
    } else {
      this.firstFocusable.focus();
    }
  },

  handleKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.closeModal();
      return;
    }

    if (e.key !== 'Tab') return;

    // Update focusable elements in case they changed
    const focusableElements = this.el.querySelectorAll(this.focusableSelector);
    if (focusableElements.length === 0) return;

    this.firstFocusable = focusableElements[0];
    this.lastFocusable = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      // Shift + Tab: Go to last element if on first
      if (document.activeElement === this.firstFocusable) {
        e.preventDefault();
        this.lastFocusable.focus();
      }
    } else {
      // Tab: Go to first element if on last
      if (document.activeElement === this.lastFocusable) {
        e.preventDefault();
        this.firstFocusable.focus();
      }
    }
  },

  handleFocusOut(e) {
    // If focus is leaving the trap, bring it back
    if (!this.el.contains(e.relatedTarget)) {
      e.preventDefault();
      const focusableElements = this.el.querySelectorAll(this.focusableSelector);
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }
  },

  closeModal() {
    // Try to find a close button
    const closeButton = this.el.querySelector('[phx-click*="close"]') ||
                       this.el.querySelector('[data-close-modal]') ||
                       this.el.querySelector('button[aria-label*="close"]');

    if (closeButton) {
      closeButton.click();
    } else {
      // Push close event to LiveView
      this.pushEvent('close_modal', {});
    }
  }
};

export default FocusTrapHook;
